import { Component, computed, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { fb } from '../../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { PersonPalliativeCareStore } from '../person-palliative-care.store';
import { FolderPalliativeCareObject } from '@libs/types/src/services/object-type/folder/folder-palliative-care.object';
import { dateify } from '../../../../../shared/helpers/date.helper';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import {
    FolderPalliativeCareCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-palliative-care.create-input';
import {
    FolderPalliativeCareUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-palliative-care.update-input';
import { contentGlobalSuccess } from '../../../../content.error';
import { BaseDialogComponent } from '../../../../../../components/kit/base-dialog/base-dialog.component';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { OrganizationGlobalStore } from '../../../../../../store/organization.global-store';
import { userQuery } from './person-palliative-care-dialog.query';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { FormatPersonPipe } from '../../../../../shared/pipes/format-person.pipe';

@Component({
    selector: 'app-person-palliative-care-dialog',
    templateUrl: './person-palliative-care-dialog.component.html',
    styleUrl: './person-palliative-care-dialog.component.scss',
    providers: [PersonPalliativeCareStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateInputComponent,
        FormatPersonPipe,
        SelectInputComponent,
        TextInputComponent,
    ],
})
export class PersonPalliativeCareDialogComponent extends BaseDialogComponent implements OnInit {
    protected readonly store = inject(PersonPalliativeCareStore);
    protected readonly organizationGlobalStore = inject(OrganizationGlobalStore);

    protected form = fb.group({
        dateFrom: fb.control<Date | null>(null, [Validators.required]),
        dateTo: fb.control<Date | null>(null),
        note: fb.control(''),
        userId: fb.control<string | null>(null, [Validators.required]),
    });

    care: FolderPalliativeCareObject | null = null;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.care = this.dialogConfig.data.care;
        if (this.care) {
            this.form.patchValue({
                ...this.care,
                dateFrom: new Date(this.care.dateFrom),
                dateTo: dateify(this.care.dateTo),
            });
        }
    }

    protected userScroll = computed(() => {
        return new InfiniteScrollStore(
            this.store.api.getUsers,
            FilterBuilder.filter({
                serviceId: this.store.globalGlobalStore.loggedUserStore.selectedServiceId(),
                doctor: true,
            }),
            true,
            userQuery,
        );
    });

    override async save() {
        const folderId = this.store.folderId();
        if (isInvalid(this.form) || !folderId) return;

        if (!this.care) {
            const data = {
                ...this.form.value,
                folderId: folderId,
                dateFrom: this.form.value.dateFrom!.toISOString(),
                dateTo: this.form.value.dateTo?.toISOString() || null,
            } as FolderPalliativeCareCreateInput;
            await this.store.api.createFolderPalliativeCare(data);
            contentGlobalSuccess.next('common.created');
        } else {
            const data = {
                id: this.care.id,
                ...this.form.value,
                dateFrom: this.form.value.dateFrom!.toISOString(),
                dateTo: this.form.value.dateTo?.toISOString() || null,
            } as FolderPalliativeCareUpdateInput;
            await this.store.api.updateFolderPalliativeCare(data);
            contentGlobalSuccess.next('common.updated');
        }
        this.dialogRef.close();
    }

    protected readonly DefaultCategoryTypeEnum = DefaultCategoryTypeEnum;
}
